import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const CART_STORAGE_KEY = 'infobloom_cart';

const CartContext = createContext(null);

function loadCart() {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveCart(items) {
  localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
}

/** Normalize course to cart item: { id, title, thumbnail, price, instructor } */
export function toCartItem(course) {
  const id = course.id != null ? String(course.id) : '';
  const instructor = typeof course.instructor === 'object' ? course.instructor?.name : course.instructor;
  return {
    id,
    title: course.title || 'Course',
    thumbnail: course.thumbnail || course.banner || '',
    price: course.price ?? 'Free',
    instructor: instructor || 'Instructor',
  };
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadCart);

  useEffect(() => {
    saveCart(items);
  }, [items]);

  const addItem = useCallback((course) => {
    const item = toCartItem(course);
    if (!item.id) return;
    setItems((prev) => {
      if (prev.some((i) => i.id === item.id)) return prev;
      return [...prev, item];
    });
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((i) => i.id !== String(id)));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const count = items.length;
  const value = { items, addItem, removeItem, clearCart, count };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
